import React, { useCallback, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';

/**
 * Monaco, with every `{{path}}` trace reference rendered as an inline pill
 * with a click-to-remove ✕ — in the text itself.
 *
 * Duplicated from the current evals' editor on purpose (the two systems
 * share no code); behaviour is the same: decorations recomputed from the
 * model text on every change, so a dragged-in reference, a pasted one and a
 * hand-typed one all pill up identically. Python mode is deliberately NOT
 * masked: `t.run.output` is the code that executes and must read as code.
 *
 * Props: value, onChange, language ('python' | 'markdown'), mode ('python' |
 * 'prompt'), height, theme ('dark' | 'light'), editorRef (a ref the parent
 * uses for insertAtCursor).
 */

const PROMPT_RE = /(\{\{\s*)([A-Za-z_][\w.[\]']*?)(\s*\}\})/g;

function matchesIn(text, mode) {
  if (mode !== 'prompt') return [];
  const re = new RegExp(PROMPT_RE.source, 'g');
  const out = [];
  let hit;
  while ((hit = re.exec(text)) !== null) {
    const start = hit.index;
    const end = start + hit[0].length;
    const preLen = hit[1].length;
    const bodyLen = hit[2].length;
    out.push({
      start, end, raw: hit[0],
      pre: { start, end: start + preLen },
      body: { start: start + preLen, end: start + preLen + bodyLen },
      post: { start: start + preLen + bodyLen, end },
    });
  }
  return out;
}

export function insertAtCursor(editor, text) {
  if (!editor) return;
  const sel = editor.getSelection();
  editor.executeEdits('insert-var', [{ range: sel, text, forceMoveMarkers: true }]);
  editor.focus();
}

/**
 * `text` with `ins` placed where the person's caret is, or on a line of its
 * own at the end when they have not been in the editor yet — for a change
 * that must land in the same update as other config (a span variable and its
 * reference together), where an editor edit would race it.
 */
export function textWithInsert(editor, text, ins) {
  const cur = String(text || '');
  const model = editor && editor.getModel && editor.getModel();
  const sel = editor && editor.getSelection && editor.getSelection();
  const touched = sel && !(sel.startLineNumber === 1 && sel.startColumn === 1 && sel.endLineNumber === 1 && sel.endColumn === 1);
  if (!model || !touched || model.getValue() !== cur) return cur.trim() ? `${cur.replace(/\s+$/, '')}\n\n${ins}\n` : ins;
  const start = model.getOffsetAt({ lineNumber: sel.startLineNumber, column: sel.startColumn });
  const end = model.getOffsetAt({ lineNumber: sel.endLineNumber, column: sel.endColumn });
  return `${cur.slice(0, start)}${ins}${cur.slice(end)}`;
}

export default function VariableEditor({ value, onChange, language, mode, height = '320px', theme, editorRef, readOnly = false }) {
  const localRef = useRef(null);
  const edRef = editorRef || localRef;
  const monacoRef = useRef(null);
  const decoIdsRef = useRef([]);
  const prevOffsetRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const paint = useCallback(() => {
    const editor = edRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel();
    if (!editor || !monaco || !model) return;
    const rangeOf = (seg) => {
      const s = model.getPositionAt(seg.start);
      const e = model.getPositionAt(seg.end);
      return new monaco.Range(s.lineNumber, s.column, e.lineNumber, e.column);
    };
    const stickiness = monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges;
    const decos = [];
    matchesIn(model.getValue(), modeRef.current).forEach((v, i) => {
      const x = { content: '✕', inlineClassName: `eval3-var-x eval3-var-i${i}` };
      const hover = { value: `\`${v.raw.trim()}\` — click ✕ to remove` };
      decos.push({ range: rangeOf(v.pre), options: { inlineClassName: 'eval3-var-syntax', stickiness } });
      decos.push({ range: rangeOf(v.body), options: { inlineClassName: 'eval3-var-pill', after: x, hoverMessage: hover, stickiness } });
      decos.push({ range: rangeOf(v.post), options: { inlineClassName: 'eval3-var-syntax', stickiness } });
    });
    decoIdsRef.current = editor.deltaDecorations(decoIdsRef.current, decos);
  }, [edRef]);

  useEffect(() => { paint(); }, [value, mode, paint]);

  return (
    <Editor
      height={height}
      defaultLanguage={language}
      language={language}
      theme={theme === 'dark' ? 'vs-dark' : 'light'}
      value={value}
      onChange={(v) => onChange(v ?? '')}
      onMount={(editor, monaco) => {
        edRef.current = editor;
        monacoRef.current = monaco;
        paint();

        // Keep the caret out of the middle of a pill: the hidden braces still
        // occupy columns, so treat a pill as one atom.
        editor.onDidChangeCursorPosition((e) => {
          const model = editor.getModel();
          if (!model) return;
          if (!editor.getSelection()?.isEmpty()) { prevOffsetRef.current = null; return; }
          const offset = model.getOffsetAt(e.position);
          const hit = matchesIn(model.getValue(), modeRef.current).find((v) => offset > v.start && offset < v.end);
          const prev = prevOffsetRef.current;
          if (!hit) { prevOffsetRef.current = offset; return; }
          const movingLeft = e.source !== 'mouse' && prev != null && offset < prev;
          const snapTo = movingLeft ? hit.start : hit.end;
          prevOffsetRef.current = snapTo;
          editor.setPosition(model.getPositionAt(snapTo));
        });

        editor.onMouseDown((e) => {
          const el = e.target?.element;
          if (!el?.classList?.contains('eval3-var-x')) return;
          const model = editor.getModel();
          if (!model) return;
          const marker = [...el.classList].find((c) => /^eval3-var-i\d+$/.test(c));
          if (!marker) return;
          const hit = matchesIn(model.getValue(), modeRef.current)[Number(marker.slice(11))];
          if (!hit) return;
          const s = model.getPositionAt(hit.start);
          const en = model.getPositionAt(hit.end);
          editor.executeEdits('remove-var', [{
            range: new monaco.Range(s.lineNumber, s.column, en.lineNumber, en.column), text: '', forceMoveMarkers: true,
          }]);
          editor.pushUndoStop();
        });

        // Own the drop: Monaco's built-in dropIntoEditor mangles the payload.
        const dom = editor.getDomNode();
        if (dom) {
          dom.addEventListener('dragover', (ev) => { ev.preventDefault(); if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy'; });
          dom.addEventListener('drop', (ev) => {
            const text = ev.dataTransfer?.getData('text/plain');
            if (!text) return;
            ev.preventDefault(); ev.stopPropagation();
            const t = editor.getTargetAtClientPoint(ev.clientX, ev.clientY);
            const pos = t?.position || editor.getPosition();
            if (!pos) return;
            editor.executeEdits('drop-var', [{
              range: new monaco.Range(pos.lineNumber, pos.column, pos.lineNumber, pos.column), text, forceMoveMarkers: true,
            }]);
            editor.pushUndoStop();
            editor.focus();
          });
        }
      }}
      options={{
        minimap: { enabled: false },
        wordWrap: 'on',
        fontSize: 13,
        scrollBeyondLastLine: false,
        dropIntoEditor: { enabled: false },
        readOnly,
        automaticLayout: true,
        // An eval step is ten lines in a panel, not a file in an IDE. Monaco's
        // defaults reserve the gutter of one: room for five digits, a folding
        // strip and a decorations margin — 65px of a 482px editor to render
        // "1". minChars is a floor, not a cap, so the numbers still grow their
        // own column at line 100.
        lineNumbersMinChars: 2,
        lineDecorationsWidth: 6,
        folding: false,
        glyphMargin: false,
        // Same waste on the right: an overview ruler maps a file you can see all of.
        overviewRulerLanes: 0,
        overviewRulerBorder: false,
        scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
      }}
    />
  );
}
